/**
 * Module 53 (WP-53.1) — which outcome popup owns the screen.
 *
 * ## The bug this exists for
 *
 * Reported, and reproduced in `Bugs/Last Move Complete.mp4`: clearing the last
 * block on the last budgeted move shows **Out of Moves!** and then, about a
 * second later, **Level Complete!**. Two popups that contradict each other, in
 * the wrong order.
 *
 * The priority the brief asks for — win first, exhausted second — was already
 * there. `_afterMove` branches `blocks.length === 0` → `hasNoFreeMoves` →
 * `isOutOfMoves`, and no `move_budget_exhausted` event fired on the reported
 * run, so that branch was never taken. The defect is not in the decision. It is
 * in what gets DRAWN, because the out-of-moves popup is *derived* rather than
 * flagged (a deliberate WP-50.28a choice, so entering a level, restarting and
 * being granted ad moves all clear it for free):
 *
 * ```ts
 * isOutOfMoves(this._state) && !this._isWon && !this._isStuck
 * ```
 *
 * On a winning last move the win is *detected* at once, but `_isWon` is not set
 * until `_showWin()` — at the END of the celebration, up to `EXIT_FX_TIMEOUT_MS`
 * of slide-out plus ~1.2s of confetti later. `_afterMove` calls `_renderHud()`
 * on the next line. For that whole window `_isWon` is false, `_isStuck` is
 * false, and `moveCount === moveBudget` is true — so the derived expression says
 * yes and the wrong popup mounts. The recording confirms the ordering: the coins
 * land (265 → 285) WHILE "Out of Moves!" is on screen, which only happens on the
 * win path.
 *
 * `_isCelebrating` is the missing term. It is a third state, held in the plugin,
 * consulted by an expression in the plugin and by three more in `GameHUD` — and
 * "add a fourth `&&`" would leave the ranking spread across four expressions in
 * two files, which is what let one of them fall behind in the first place.
 *
 * ## So the ranking is a function
 *
 * One pure selector, no plugin import, exhaustively tested as a table. The
 * plugin's getters read it and so does the HUD, so the two cannot disagree, and
 * the next state somebody adds has exactly one site to update.
 *
 * ## Why `blocksLeft` is in here
 *
 * `isCelebrating` alone would fix the reported bug and would still be a fact
 * about a TIMER. An empty board is the fact about the GAME: there is nothing
 * left to move, so no number of extra moves could matter and the out-of-moves
 * popup is wrong whatever any flag says. With it, the win outranks the wall by
 * construction rather than by two booleans being set in the right order.
 *
 * Callers that do not know the board (the HUD is one — it receives no block
 * count, and adding a prop so it could re-derive an answer the plugin already
 * has would be two sources of truth) simply omit it.
 *
 * Unrelated to {@link ./popup-stack}, which owns the *navigable* surfaces
 * (Info / Settings / Language) and their Back behaviour. These four are
 * outcomes: nothing navigates to them and nothing navigates back.
 */
/**
 * Rank the outcome states. The order is the whole point:
 *
 * 1. **win** — the popup is up.
 * 2. **celebrating** — the win path owns the screen; an empty board counts even
 *    if no flag has been set yet.
 * 3. **stuck** — no legal move left, so more moves would sell nothing.
 * 4. **outOfMoves** — the budget is spent on a board that could still be played.
 */
export function activeModal(view) {
    if (view.isWon)
        return 'win';
    if (view.isCelebrating || view.blocksLeft === 0)
        return 'celebrating';
    if (view.isStuck)
        return 'stuck';
    if (view.budgetSpent)
        return 'outOfMoves';
    return null;
}
/**
 * Is a modal DIALOG on screen — as opposed to the celebration, which covers the
 * board with a transparent skip layer and traps nothing?
 *
 * The distinction matters to `GameHUD`'s `isDialogOpen`, which suppresses
 * transient chrome (WP-50.30) and gates the onboarding coach. The celebration is
 * already handled there by its own term, and folding it in here would change
 * that predicate's meaning rather than its correctness.
 */
export function isPopupModal(modal) {
    return modal !== null && modal !== 'celebrating';
}
